/** Hotel completed the revenue form (`validated` / `no_production`); `invoice_sent` counts the same for KPIs/materialization. */
export function isRevenueCampaignHotelFormComplete(status: string): boolean {
  return (
    status === 'validated' ||
    status === 'no_production' ||
    status === 'invoice_sent'
  );
}

export function isCampaignHotelNoProduction(status: string): boolean {
  return status === 'no_production';
}

export function isCampaignHotelCancelled(status: string): boolean {
  return status === 'cancelled';
}

/** Hotel submitted via form or on-behalf, not yet admin-validated. */
export function isCampaignHotelSubmitted(
  status: string,
  validatedBy: string | null | undefined,
): boolean {
  return status === 'validated' && !validatedBy;
}

const OUTREACH_CANCELLABLE_STATUSES = new Set([
  'pending',
  'email_sent',
  'bounced',
  'reminder_1',
  'reminder_2',
  'reminder_3',
]);

export function isCampaignHotelOutreachCancellable(status: string): boolean {
  return OUTREACH_CANCELLABLE_STATUSES.has(status);
}

export function canAdminCancelCampaignHotel(params: {
  status: string;
  validated_by: string | null | undefined;
}): boolean {
  const { status, validated_by } = params;
  if (isCampaignHotelOutreachCancellable(status)) return true;
  if (isCampaignHotelNoProduction(status)) return true;
  return isCampaignHotelSubmitted(status, validated_by);
}

export function canAdminUndoCancelCampaignHotel(status: string): boolean {
  return status === 'cancelled';
}

export type CampaignHotelStatusAfterUndoCancel =
  | 'validated'
  | 'no_production'
  | 'reminder_3'
  | 'reminder_2'
  | 'reminder_1'
  | 'email_sent'
  | 'pending';

export function statusAfterUndoCancel(params: {
  validated_at: string | null | undefined;
  validated_by?: string | null | undefined;
  total_realised?: number | null | undefined;
  email_sent_at?: string | null | undefined;
  reminder_1_at?: string | null | undefined;
  reminder_2_at?: string | null | undefined;
  reminder_3_at?: string | null | undefined;
}): CampaignHotelStatusAfterUndoCancel {
  const hasAdminValidation =
    params.validated_by != null && String(params.validated_by).trim() !== '';
  if (params.validated_at && hasAdminValidation) {
    const total = params.total_realised ?? 0;
    return total === 0 ? 'no_production' : 'validated';
  }
  if (params.validated_at) return 'validated';
  if (params.reminder_3_at) return 'reminder_3';
  if (params.reminder_2_at) return 'reminder_2';
  if (params.reminder_1_at) return 'reminder_1';
  if (params.email_sent_at) return 'email_sent';
  return 'pending';
}

export function shouldClearRemindersCancelledAfterUndoCancel(
  status: CampaignHotelStatusAfterUndoCancel,
): boolean {
  return status !== 'validated' && status !== 'no_production';
}

export function isCampaignHotelIncludedInKpis(status: string): boolean {
  return !isCampaignHotelCancelled(status);
}

/** Dashboard submission-complete statuses (validated, no production, or invoiced). */
export function isRevenueHotelSubmissionCompleteStatus(status: string): boolean {
  return isRevenueCampaignHotelFormComplete(status);
}

/**
 * Campaign / hotel materialization %: realised ÷ estimated, rounded to an integer.
 * Null when there is no estimated baseline. Can exceed 100%.
 */
export function revenueCampaignMaterializationPct(
  realised: number,
  estimated: number,
): number | null {
  if (estimated <= 0) return null;
  return Math.round((realised / estimated) * 100);
}

/** Admin-validated terminal status from total realised (matches edge function). */
export function adminValidatedStatusFromRealisedTotal(
  totalRealised: number,
  existingStatus: string,
): 'invoice_sent' | 'no_production' | 'validated' {
  if (existingStatus === 'invoice_sent') return 'invoice_sent';
  return totalRealised === 0 ? 'no_production' : 'validated';
}
